<template>
  <InputText
    v-if="!configurable && !format"
    v-bind="$attrs"
    v-model="modelValue"
    :disabled="disabled"
    :placeholder="placeholder"
  >
    <template v-if="$slots['before-input']" #before-input><slot name="before-input" /></template>
  </InputText>
  <div v-else ref="root" class="input-text reference-format-input w-full">
    <div ref="anchor" class="text-on-surface relative w-full rounded-md">
      <div v-if="$slots['before-input']" class="absolute top-1/2 left-2 -translate-y-1/2">
        <slot name="before-input" />
      </div>
      <div
        v-if="editing || (!configurable && modelValue)"
        aria-hidden="true"
        class="reference-paint pointer-events-none absolute inset-y-0 left-2 flex items-center overflow-hidden text-base leading-none whitespace-pre sm:text-sm"
        :class="{
          'right-36': configurable,
          'right-2': !configurable
        }"
        :style="{ left: $slots['before-input'] ? `${iconPadding}px` : '8px' }"
      >
        <div class="reference-paint-text flex">
          <span
            v-for="(character, index) in displayedCharacters"
            :key="index"
            :class="{
              'text-on-surface/45': editing && !positions[index]?.fixed,
              'text-error underline': !editing && !characterMatches[index]
            }"
            >{{ character }}</span
          >
          <span v-if="!editing" class="text-on-surface/45">{{ remainingPlaceholder }}</span>
        </div>
      </div>
      <input
        ref="input"
        v-bind="$attrs"
        :value="editing ? example : modelValue"
        :placeholder="example || placeholder"
        :disabled="disabled"
        :readonly="configurable"
        :aria-invalid="hasMismatch"
        :aria-describedby="statusId"
        autocomplete="off"
        spellcheck="false"
        draggable="false"
        class="surface focus:ring-secondary caret-on-surface block w-full rounded-md border-none px-2 py-1 leading-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-45 sm:text-sm"
        :class="{
          'pl-8!': $slots['before-input'],
          'pr-36!': configurable,
          'text-transparent!': editing || (!configurable && modelValue),
          'ring-error focus:ring-error ring-1': hasMismatch
        }"
        @input="onInput"
        @scroll="onScroll"
        @keydown="onKeydown"
      />
      <button
        v-if="configurable"
        ref="trigger"
        type="button"
        class="surface text-primary absolute inset-y-0.5 right-1 flex items-center gap-1.5 rounded px-1.5 text-xs disabled:opacity-45"
        :disabled="disabled"
        :aria-expanded="editing"
        :aria-controls="pickerId"
        @click="editing = !editing"
      >
        Format kiezen <FontAwesomeIcon :icon="['fas', 'chevron-down']" />
      </button>
    </div>
    <span :id="statusId" class="sr-only" aria-live="polite">{{ accessibleStatus }}</span>
    <Teleport to="body">
      <div
        v-if="editing"
        :id="pickerId"
        ref="picker"
        class="reference-format-picker surface text-on-surface fixed z-[90] rounded-md border p-2 text-xs shadow-lg"
        role="group"
        aria-label="Format kiezen"
        @keydown.esc.stop.prevent="finish"
      >
        <div class="mb-2 grid grid-cols-4 gap-1" aria-label="Type positie">
          <button
            v-for="option in typeOptions"
            :key="option.kind"
            type="button"
            class="rounded px-1 py-1.5"
            :class="kind === option.kind ? 'primary' : 'gray-lighten-2'"
            :aria-pressed="kind === option.kind"
            @click="kind = option.kind"
          >
            {{ option.label }}
          </button>
        </div>
        <button
          type="button"
          class="gray-lighten-2 mb-2.5 flex w-full items-center justify-between rounded px-2 py-1.5 disabled:opacity-45"
          :disabled="!canAppend({ kind })"
          @click="append({ kind })"
        >
          <span>{{ selectedType.anyLabel }}</span
          ><span class="text-on-surface/50">{{ selectedType.hint }}</span>
        </button>
        <span class="text-on-surface/55 mb-1.5 block">Vaste waarde</span>
        <div class="grid grid-cols-7 gap-1">
          <button
            v-for="character in selectedType.characters"
            :key="character"
            type="button"
            class="reference-character rounded border py-1 disabled:opacity-45"
            :aria-label="`Vast: ${character === ' ' ? 'spatie' : character}`"
            :disabled="!canAppend(fixedItem(character))"
            @click="append(fixedItem(character))"
          >
            {{ character === " " ? "␣" : character }}
          </button>
        </div>
        <div
          class="reference-picker-footer mt-2 flex items-center justify-between gap-1 border-t pt-2"
        >
          <button
            type="button"
            class="rounded p-1.5 disabled:opacity-45"
            aria-label="Laatste positie verwijderen"
            :disabled="!items.length"
            @click="removeLast"
          >
            <FontAwesomeIcon :icon="faDeleteLeft" />
          </button>
          <button type="button" class="text-on-surface/55 rounded p-1.5" @click="format = null">
            Opnieuw
          </button>
          <button type="button" class="primary rounded px-2.5 py-1.5" @click="finish">Klaar</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, useId, watch } from "vue"
import { useEventListener } from "@vueuse/core"
import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faDeleteLeft } from "@fortawesome/free-solid-svg-icons"
import InputText from "@/components/inputs/InputText.vue"
import {
  parsePurchaseOrderReferenceFormat,
  serializePurchaseOrderReferenceFormat,
  purchaseOrderReferenceFormatExample,
  purchaseOrderReferenceFormatPositions,
  purchaseOrderReferenceCharacterMatches,
  type PurchaseOrderReferenceFormatItem
} from "@/helper/validation/purchase-order-reference-format"

defineOptions({ inheritAttrs: false })
const props = withDefaults(
  defineProps<{
    configurable?: boolean
    disabled?: boolean
    placeholder?: string
    iconPadding?: number
  }>(),
  {
    configurable: false,
    disabled: false,
    placeholder: "",
    iconPadding: 32
  }
)
const modelValue = defineModel<string | null | undefined>({ default: "" })
const format = defineModel<string | null>("format", { default: null })
const editing = ref(false)
const kind = ref<"uppercase" | "lowercase" | "digit" | "symbol">("uppercase")
const root = ref<HTMLElement>()
const anchor = ref<HTMLElement>()
const picker = ref<HTMLElement>()
const input = ref<HTMLInputElement>()
const trigger = ref<HTMLButtonElement>()
const paintScroll = ref(0)
const id = useId()
const pickerId = `reference-format-picker-${id}`
const statusId = `reference-format-status-${id}`
const typeOptions = [
  {
    kind: "uppercase" as const,
    label: "A–Z",
    anyLabel: "Iedere hoofdletter",
    hint: "A–Z",
    characters: [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"]
  },
  {
    kind: "lowercase" as const,
    label: "a–z",
    anyLabel: "Iedere kleine letter",
    hint: "a–z",
    characters: [..."abcdefghijklmnopqrstuvwxyz"]
  },
  {
    kind: "digit" as const,
    label: "0–9",
    anyLabel: "Ieder cijfer",
    hint: "0–9",
    characters: [..."0123456789"]
  },
  {
    kind: "symbol" as const,
    label: "#+=",
    anyLabel: "Ieder teken",
    hint: "/ − . …",
    characters: ["/", "-", ".", "_", "+", "(", ")", ":", " ", "#", "&"]
  }
]
const selectedType = computed(() => typeOptions.find((option) => option.kind === kind.value)!)
const items = computed(() => parsePurchaseOrderReferenceFormat(format.value))
const example = computed(() => purchaseOrderReferenceFormatExample(format.value) || "")
const positions = computed(() => purchaseOrderReferenceFormatPositions(format.value))
const displayedCharacters = computed(() => [
  ...(editing.value ? example.value : modelValue.value || "")
])
const characterMatches = computed(() =>
  purchaseOrderReferenceCharacterMatches(modelValue.value, format.value)
)
const hasMismatch = computed(
  () => !props.configurable && characterMatches.value.some((matches) => !matches)
)
const remainingPlaceholder = computed(() =>
  [...example.value].slice(displayedCharacters.value.length).join("")
)
const accessibleStatus = computed(() =>
  editing.value
    ? `Format: ${example.value || "leeg"}. Kies de volgende positie.`
    : hasMismatch.value
      ? "Deze positie past niet bij het gekozen format."
      : `Verwacht format: ${example.value || "geen beperking"}`
)

function fixedItem(character: string): PurchaseOrderReferenceFormatItem {
  return {
    kind: kind.value === "uppercase" || kind.value === "lowercase" ? "exact" : "fixed",
    value: character
  }
}
function canAppend(item: PurchaseOrderReferenceFormatItem) {
  return (serializePurchaseOrderReferenceFormat([...items.value, item])?.length || 0) <= 255
}
function append(item: PurchaseOrderReferenceFormatItem) {
  if (canAppend(item)) format.value = serializePurchaseOrderReferenceFormat([...items.value, item])
}
function removeLast() {
  format.value = serializePurchaseOrderReferenceFormat(items.value.slice(0, -1))
}
function finish() {
  editing.value = false
  trigger.value?.focus()
}
function onInput(event: Event) {
  modelValue.value = (event.target as HTMLInputElement).value
}
function onScroll(event: Event) {
  paintScroll.value = (event.target as HTMLInputElement).scrollLeft
}
function onKeydown(event: KeyboardEvent) {
  if (editing.value && event.key === "Backspace") {
    event.preventDefault()
    removeLast()
  }
  if (editing.value && event.key === "Escape") {
    event.stopPropagation()
    event.preventDefault()
    finish()
  }
}
useEventListener(document, "pointerdown", (event) => {
  const path = event.composedPath()
  if (editing.value && !path.includes(root.value!) && !path.includes(picker.value!))
    editing.value = false
})
useEventListener(document, "focusin", (event) => {
  const target = event.target as Node
  if (editing.value && !root.value?.contains(target) && !picker.value?.contains(target))
    editing.value = false
})
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) editing.value = false
  }
)
let stopPositioning: (() => void) | undefined
watch(editing, async (open) => {
  stopPositioning?.()
  stopPositioning = undefined
  if (!open) return
  await nextTick()
  if (!editing.value || !anchor.value || !picker.value) return
  const reference = anchor.value,
    floating = picker.value
  floating.style.width = `${Math.min(264, reference.clientWidth, window.innerWidth - 16)}px`
  const update = async () => {
    const position = await computePosition(reference, floating, {
      placement: "bottom-end",
      strategy: "fixed",
      middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })]
    })
    Object.assign(floating.style, { left: `${position.x}px`, top: `${position.y}px` })
  }
  stopPositioning = autoUpdate(reference, floating, () => {
    void update()
  })
})
onUnmounted(() => stopPositioning?.())
</script>

<style scoped>
.reference-paint-text {
  transform: translateX(v-bind("`${-paintScroll}px`"));
}
.reference-format-picker,
.reference-character,
.reference-picker-footer {
  border-color: color-mix(in srgb, var(--color-primary) 15%, transparent);
}
.reference-character {
  min-height: 30px;
}
@media (max-width: 480px) {
  .reference-character,
  .reference-format-picker button {
    min-height: 36px;
  }
}
</style>
